<template>
  <div class="data-table">
    <!-- Toolbar -->
    <div class="table-toolbar">
      <div class="toolbar-left">
        <h4 v-if="title">{{ title }}</h4>
        <span class="record-count" v-if="total">{{ total }} records</span>
        <slot name="toolbar-left"></slot>
      </div>
      <div class="toolbar-right">
        <slot name="toolbar-right"></slot>
        <div class="search-field" v-if="searchable">
          <md-icon>search</md-icon>
          <input
              type="text"
              v-model="searchQuery"
              :placeholder="searchPlaceholder || 'Search...'"
              @input="onSearch"
              class="search-input"
          />
          <button
              v-if="searchQuery"
              class="search-clear"
              type="button"
              @click="clearSearch"
              title="Clear search"
          >
            <md-icon>close</md-icon>
          </button>
        </div>
      </div>
    </div>

    <div class="table-body-wrapper">
      <div v-if="loading" class="table-loading-overlay">
        <div class="loading-content">
          <md-progress-spinner
              md-mode="indeterminate"
              :md-diameter="50"
              :md-stroke="3"
          ></md-progress-spinner>
          <p>{{ loadingText }}</p>
        </div>
      </div>

      <div class="table-wrapper" :class="{ 'is-loading': loading }">
        <md-table>
          <md-table-row>
            <md-table-head
                v-for="col in columns"
                :key="col.field"
                :class="col.align || 'left'"
            >
              {{ col.label }}
            </md-table-head>
            <md-table-head v-if="hasActions" class="text-center">Actions</md-table-head>
          </md-table-row>

          <md-table-row v-for="(item, index) in data" :key="item.id || index">
            <md-table-cell
                v-for="col in columns"
                :key="col.field"
                :class="col.align || 'left'"
            >
              <slot :name="'cell-' + col.field" :item="item" :value="getValue(item, col.field)">
                {{ getValue(item, col.field) }}
              </slot>
            </md-table-cell>
            <md-table-cell v-if="hasActions" class="text-center">
              <div class="action-buttons">
                <slot name="actions" :item="item"></slot>
              </div>
            </md-table-cell>
          </md-table-row>

          <tr v-if="!data || data.length === 0">
            <td :colspan="columns.length + (hasActions ? 1 : 0)" class="empty-state">
              <md-icon>inbox</md-icon>
              <p>{{ emptyMessage || 'No data found' }}</p>
              <slot name="empty-actions"></slot>
            </td>
          </tr>
        </md-table>
      </div>

      <!-- Footer -->
      <div class="table-footer" v-if="showPagination && total > 0">
        <div class="pagination-info">
          Showing {{ from + 1 }} to {{ to }} of {{ total }} entries
        </div>
        <div class="pagination-controls">
          <slot name="pagination-left"></slot>
          <div class="per-page-wrapper" v-if="showPerPage">
            <label>Per page</label>
            <select v-model.number="perPage" class="per-page-select">
              <option v-for="item in perPageOptions" :key="item" :value="item">
                {{ item }}
              </option>
            </select>
          </div>
          <pagination
              class="pagination-no-border pagination-success"
              v-model="currentPage"
              :per-page="perPage"
              :total="total"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination';
import _ from 'lodash';

export default {
  name: 'TableData',
  components: { Pagination },
  props: {
    title: { type: String, default: '' },
    columns: { type: Array, required: true },
    data: { type: Array, default: function () { return []; } },
    total: { type: Number, default: 0 },
    loading: { type: Boolean, default: false },
    loadingText: { type: String, default: 'Loading...' },
    searchable: { type: Boolean, default: true },
    searchPlaceholder: { type: String, default: 'Search...' },
    showPagination: { type: Boolean, default: true },
    showPerPage: { type: Boolean, default: true },
    perPageOptions: {
      type: Array,
      default: function () { return [5, 10, 25, 50]; }
    },
    defaultPerPage: { type: Number, default: 10 },
    emptyMessage: { type: String, default: 'No data found' },
    hasActions: { type: Boolean, default: true },
    storageKey: { type: String, default: '' },
    storageEnabled: { type: Boolean, default: true },
    serverSide: { type: Boolean, default: false }
  },
  data() {
    return {
      searchQuery: '',
      currentPage: 1,
      perPage: this.defaultPerPage
    };
  },
  computed: {
    from() {
      return this.perPage * (this.currentPage - 1);
    },
    to() {
      var highBound = this.from + this.perPage;
      if (this.total < highBound) highBound = this.total;
      return highBound;
    },
    effectiveStorageKey() {
      if (this.storageKey) return 'data-table:' + this.storageKey;
      var routeName = (this.$route && this.$route.name) ? this.$route.name : 'default';
      return 'data-table:' + routeName;
    }
  },
  created() {
    this.restoreState();
    this.$nextTick(this.emitFetch);
  },
  methods: {
    getValue(item, field) {
      if (!field) return null;
      if (field.indexOf('.') !== -1) {
        return field.split('.').reduce(function (obj, key) {
          return obj && obj[key] !== undefined ? obj[key] : null;
        }, item);
      }
      return item[field];
    },

    onSearch: _.debounce(function () {
      this.currentPage = 1;
      this.persistState();
      this.emitFetch();
      this.$emit('search', this.searchQuery);
    }, 300),

    clearSearch() {
      this.searchQuery = '';
      this.currentPage = 1;
      this.persistState();
      this.emitFetch();
      this.$emit('search', '');
    },

    emitFetch() {
      this.$emit('fetch', {
        page: this.currentPage,
        perPage: this.perPage,
        search: this.searchQuery
      });
    },

    persistState() {
      if (!this.storageEnabled || typeof sessionStorage === 'undefined') return;
      try {
        sessionStorage.setItem(this.effectiveStorageKey, JSON.stringify({
          searchQuery: this.searchQuery,
          currentPage: this.currentPage,
          perPage: this.perPage
        }));
      } catch (e) { /* ignore */ }
    },

    restoreState() {
      if (!this.storageEnabled || typeof sessionStorage === 'undefined') return;
      try {
        var raw = sessionStorage.getItem(this.effectiveStorageKey);
        if (!raw) return;
        var parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          if (typeof parsed.searchQuery === 'string') this.searchQuery = parsed.searchQuery;
          if (typeof parsed.currentPage === 'number' && parsed.currentPage > 0) this.currentPage = parsed.currentPage;
          if (typeof parsed.perPage === 'number' && this.perPageOptions.indexOf(parsed.perPage) !== -1) {
            this.perPage = parsed.perPage;
          }
        }
      } catch (e) { /* ignore */ }
    },

    clearState() {
      if (typeof sessionStorage !== 'undefined') {
        try { sessionStorage.removeItem(this.effectiveStorageKey); } catch (e) { /* ignore */ }
      }
    },

    reset() {
      this.searchQuery = '';
      this.currentPage = 1;
      this.perPage = this.defaultPerPage;
      this.clearState();
      this.emitFetch();
      this.$emit('reset');
    },

    setSearch(query) {
      this.searchQuery = query || '';
      this.currentPage = 1;
      this.persistState();
      this.emitFetch();
    },

    getState() {
      return {
        page: this.currentPage,
        perPage: this.perPage,
        search: this.searchQuery
      };
    }
  },
  watch: {
    currentPage: function (newPage, oldPage) {
      if (newPage === oldPage) return;
      this.persistState();
      this.emitFetch();
      this.$emit('page-change', newPage);
    },
    perPage: function () {
      this.currentPage = 1;
      this.persistState();
      this.emitFetch();
      this.$emit('per-page-change', this.perPage);
    },
    data: {
      handler: function () {
        var lastPage = this.total > 0 ? Math.ceil(this.total / this.perPage) : 1;
        if (this.currentPage > lastPage) this.currentPage = lastPage;
      }
    },
    total: {
      handler: function () {
        var lastPage = this.total > 0 ? Math.ceil(this.total / this.perPage) : 1;
        if (this.currentPage > lastPage) this.currentPage = lastPage;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.data-table {

  width: 100%;
  .table-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 0 16px 0;
    flex-wrap: wrap;
    gap: 12px;

    .toolbar-left {
      display: flex;
      align-items: center;
      gap: 12px;

      h4 {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #2d3748;
      }

      .record-count {
        font-size: 12px;
        color: #999;
        background: #f5f5f5;
        padding: 2px 10px;
        border-radius: 12px;
      }
    }

    .toolbar-right {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;

      .search-field {
        position: relative;
        width: 240px;

        > .md-icon {
          position: absolute;
          left: 10px;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
          z-index: 1;
          pointer-events: none;
          font-size: 18px;
        }

        .search-input {
          width: 100%;
          padding: 8px 36px 8px 36px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;

          &:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          }

          &::placeholder { color: #999; }
        }

        .search-clear {
          position: absolute;
          right: 6px;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          cursor: pointer;
          color: #999;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;

          &:hover {
            color: #f44336;
            background: rgba(244, 67, 54, 0.08);
          }

          .md-icon { font-size: 16px; }
        }
      }
    }
  }

  .table-body-wrapper {
    position: relative;
    min-height: 200px;
  }

  .table-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    border-radius: 8px;
  }

  .loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;

    p {
      margin: 0;
      font-size: 14px;
      font-weight: 500;
      color: #666;
    }
  }

  .table-wrapper {
    overflow-x: auto;
    transition: opacity 0.2s ease;

    &.is-loading {
      opacity: 0.35;
      pointer-events: none;
      user-select: none;
    }

    .md-table {
      width: 100%;

      .md-table-head {
        font-weight: 600;
        color: #555;
        padding: 12px 16px;
        border-bottom: 2px solid #e8e8e8;

        &.text-center { text-align: center; }
        &.text-right { text-align: right; }
      }

      .md-table-cell {
        padding: 12px 16px;
        border-bottom: 1px solid #f0f0f0;

        &.text-center { text-align: center; }
        &.text-right { text-align: right; }
      }

      .action-buttons {
        display: flex;
        justify-content: center;
        gap: 4px;

        .md-icon-button {
          width: 32px;
          height: 32px;
          min-width: 32px;
        }
      }

      .empty-state {
        text-align: center;
        padding: 60px 20px;
        color: #999;

        .md-icon {
          font-size: 48px;
          width: 48px;
          height: 48px;
          color: #ddd;
        }

        p { margin: 12px 0 16px; }
      }
    }
  }

  .table-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
    flex-wrap: wrap;
    gap: 12px;

    .pagination-info {
      font-size: 13px;
      color: #666;
    }

    .pagination-controls {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;

      .per-page-wrapper {
        display: flex;
        align-items: center;
        gap: 8px;

        label {
          font-size: 13px;
          color: #666;
          margin: 0;
        }

        .per-page-select {
          padding: 6px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 13px;
          outline: none;
          background: white;
          cursor: pointer;

          &:focus { border-color: #667eea; }
        }
      }
    }
  }
}
</style>